import { Injectable } from '@angular/core';
import { Client, Message } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject } from 'rxjs';
import { API_BASE_URL } from './url';


export interface PresenceEvent { bwariId: string; status: 'ONLINE' | 'OFFLINE'; }
export interface ChatMessage { senderId: string; receiverId: string; content: string; timestamp?: string; }
export interface TypingEvent { senderId: string; receiverId: string; isTyping: boolean; }

@Injectable({
  providedIn: 'root'
})
export class RealTimeService {
  private client!: Client;
  private typingTimeout: any;
  
  // Observables for your components to subscribe to
  public presenceUpdates$ = new BehaviorSubject<PresenceEvent | null>(null);
  public incomingMessages$ = new BehaviorSubject<ChatMessage | null>(null);
  public typingIndicator$ = new BehaviorSubject<TypingEvent | null>(null);

  constructor() {
    // Gracefully disconnect when the user closes the browser tab/window.
    // This tells the backend immediately that the user is offline,
    // rather than waiting for the server-side timeout.
    window.addEventListener('beforeunload', () => {
      this.disconnect();
    });
  }

  public connect() {
    const token = localStorage.getItem('bwari_jwt_token');
    if (!token) return;

    this.client = new Client({
      // We use SockJS as the transport protocol, pointing to your Spring Boot /ws endpoint
      webSocketFactory: () => new SockJS(`${API_BASE_URL}/ws`),
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      debug: (str) => { console.log(str); },
      reconnectDelay: 5000, // Auto-reconnect if internet drops
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    this.client.onConnect = (frame) => {
      console.log('Connected to Bwari Real-Time Engine!');

      // 1. Listen for global online/offline events
      this.client.subscribe('/topic/presence', (message: Message) => {
        this.presenceUpdates$.next(JSON.parse(message.body));
      });

      // 2. Listen for private chat messages sent to YOU
      this.client.subscribe(`/user/queue/messages`, (message: Message) => {
        this.incomingMessages$.next(JSON.parse(message.body));
      });

      // 3. Listen for typing indicators meant for YOU
      this.client.subscribe(`/user/queue/typing`, (message: Message) => {
        const typingEvent: TypingEvent = JSON.parse(message.body);
        this.typingIndicator$.next(typingEvent);

        // Auto-clear typing indicator if they stop typing for 2 seconds
        if (typingEvent.isTyping) {
          clearTimeout(this.typingTimeout);
          this.typingTimeout = setTimeout(() => {
            this.typingIndicator$.next({ ...typingEvent, isTyping: false });
          }, 2000);
        }
      });
    };

    this.client.activate();
  }

  public disconnect() {
    if (this.client && this.client.connected) {
      this.client.deactivate();
      console.log('Disconnected from Bwari Real-Time Engine.');
    }
  }

  // --- ACTIONS ---

  public sendMessage(chatMessage: ChatMessage) {
    if (this.client && this.client.connected) {
      this.client.publish({
        destination: '/app/chat.sendMessage',
        body: JSON.stringify(chatMessage)
      });
    }
  }

  public sendTypingIndicator(typingEvent: TypingEvent) {
    if (this.client && this.client.connected) {
      this.client.publish({
        destination: '/app/chat.typing',
        body: JSON.stringify(typingEvent)
      });
    }
  }
}